import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { FC } from 'react';
import { MarketSupplier } from '../../../app/services/api/orderManagementClient';
import SupplierItemRow from './SupplierItemRow';
import { useTranslation } from 'react-i18next';
import { Pill } from '../../../components/shared/Pill';
import { TableLoading } from '../../../components/forms/TableLoading';
import { EmptyRows } from '../../../components/forms/EmptyRows';
import { EmptyTable } from '../../../components/shared/EmptyTable';
import LoadingWheel from '../../../components/ui/LoadingWheel';

type Props = {
  list: MarketSupplier[];
  isLoading: boolean;
  totalCount: number;
  onClear: () => void;
  onCreateNew: () => void;
};

const SuppliersList: FC<Props> = ({
  list,
  isLoading,
  totalCount,
  onClear,
  onCreateNew,
}) => {
  const { t } = useTranslation('suppliers');

  if (totalCount === 0 && !isLoading) {
    return (
      <EmptyTable
        bodyRowHeight={40}
        headerHeight={45}
        text={t('suppliers')}
        searchTerm={''}
        onClear={onClear}
        onAdd={onCreateNew}
        addItemText={t('createNew')}
        onAddIsCreating={false}
      />
    );
  }

  return (
    <>
      <Box
        sx={{
          p: 5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {/* Left: Title and Pill */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="textMD"
            fontWeight={600}>
            {t('suppliers')}
          </Typography>
          {(totalCount ?? 0) > 0 && <Pill text={`${totalCount} ${t('suppliers')}`} />}
        </Box>
      </Box>
      
      {isLoading ? (
        <LoadingWheel />
      ) : (
        <TableContainer>
          <Table sx={{ borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('supplierNumber')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('supplierName')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('emailAddress')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('fileFormat')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('publishMethod')}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ whiteSpace: 'nowrap' }}>
                  {t('cutOffTime')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('instanceName')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('marketName')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.length > 0 ? (
                <>
                  {list.map((supplier) => (
                    <SupplierItemRow
                      key={supplier.supplierId}
                      supplier={supplier}
                    />
                  ))}
                  <EmptyRows
                    numberOfCurrentRows={list.length}
                    height={40}
                  />
                </>
              ) : (
                <TableRow>
                  <TableCell 
                    colSpan={8} 
                    sx={{ textAlign: 'center', py: 4 }}
                  >
                    {t('noSuppliersFound')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

export default SuppliersList;
